import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import { StartNodeTrigger } from '..'
import { NodeDefinition } from '../../../types'
import { StartEditorNode } from '../start.editorNode'
import { StartNodeIcon } from '../start.icon'
import { StartInvoiceSentConfigForm } from './startInvoiceSent.configForm'
import {
  createSimpleTask,
  getTaskReferenceName,
  wrapVariables
} from 'Automations/nodeDefinitions/conductor'
import { handlesConnected, triggerTypeRequired } from '../start.validators'
import { InvoiceSentExecutionSummary } from './InvoiceSent.executionSummary'

import {
  equalsAmount,
  lessThanAmount,
  moreThanAmount
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import {
  amountWithUnitInput,
  formatAmountWithUnit
} from '../../switch/components/GroupEditor/inputs'
import { customerConditionalProperty } from '../conditionalProperties/customer/customer.conditionalProperty'
import { fetchCustomerCustomPropertyKeys } from '../conditionalProperties/customer/fetchCustomerCustomPropertyKeys'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

export type Config = {
  name: string
  trigger: StartNodeTrigger
}

// Bound at module scope so the component identity stays stable across renders.
const invoiceValueInput = amountWithUnitInput({ allowCurrency: true })

// Three-column layout for amount-with-unit operators: the operator dropdown
// flexes to fill, then a fixed-width unit selector and value input. The
// `valueInputComponent` factory renders its children with `gridArea: 'unit'`
// and `gridArea: 'value'`.
const amountWithUnitGridStyle = () => ({
  gridTemplateAreas: '"operator unit value"',
  gridTemplateColumns: '1fr 80px 104px',
  gap: '8px'
})

type Outputs =
  | 'invoiceId'
  | 'grossTotal'
  | 'currency'
  | 'dueDate'
  | 'customerId'
  | 'customProperties'

type TaskName = 'getInvoice' | 'getCustomer'

const taskReferenceBuilder =
  getTaskReferenceName<TaskName>('START_INVOICE_SENT')

// Async registry entry: the CUSTOMER conditional property generates one
// operator per customer custom-property key, which must be fetched first. The
// whole definition is produced once at load (see `resolveNodeDefinitions`).
export const startInvoiceSentNodeDefinition: () => Promise<
  NodeDefinition<Config, string, Outputs>
> = async () => {
  const customPropertyKeys = await fetchCustomerCustomPropertyKeys()

  return {
    name: 'Start',
    startNodeTriggerType: 'INVOICE_SENT',
    description: 'Invoice sent',
    editorNode: StartEditorNode,
    icon: StartNodeIcon,
    configForm: StartInvoiceSentConfigForm,
    defaultConfig: {
      name: '',
      trigger: 'INVOICE_SENT'
    },
    validation: {
      node: { ERROR: [], WARN: [] },
      config: {
        name: { ERROR: [], WARN: [] },
        trigger: { ERROR: [triggerTypeRequired], WARN: [] }
      },
      handles: { ERROR: [handlesConnected], WARN: [] }
    },
    isPersistent: true,
    outputMapping: nodeId => {
      const taskReferenceName = taskReferenceBuilder(nodeId)

      return {
        invoiceId: {
          id: `${taskReferenceName('getInvoice')}.output.invoice.id`,
          semanticType: semanticTypes.sequence.invoice.id
        },
        grossTotal: {
          id: `${taskReferenceName('getInvoice')}.output.invoice.grossTotal`,
          semanticType: semanticTypes.sequence.invoice.grossTotal
        },
        currency: {
          id: `${taskReferenceName('getInvoice')}.output.invoice.currency`,
          semanticType: semanticTypes.sequence.invoice.currency
        },
        dueDate: {
          id: `${taskReferenceName('getInvoice')}.output.invoice.dueDate`,
          semanticType: semanticTypes.sequence.invoice.dueDate
        },
        // The customer id is read directly off the invoice so the CUSTOMER
        // conditional property can compare it against the selected ids.
        customerId: {
          id: `${taskReferenceName('getInvoice')}.output.invoice.customerId`,
          semanticType: semanticTypes.sequence.invoice.customerId
        },
        // The full customer is fetched so the CUSTOMER property's Custom
        // property operator can compare against the customer's custom properties.
        customProperties: {
          id: `${taskReferenceName('getCustomer')}.output.customer.customProperties`,
          semanticType: semanticTypes.sequence.customer.customProperties
        }
      }
    },
    taskTransformer: ({ nodeId }) => {
      const taskReferenceName = taskReferenceBuilder(nodeId)

      return [
        createSimpleTask({
          name: 'get_sequence_data',
          taskReferenceName: taskReferenceName('getInvoice'),
          inputParameters: {
            taskExecutorConfiguration: {
              executor: TASK_EXECUTORS.invoice,
              version: 1
            },
            data: wrapVariables({
              invoiceId: 'workflow.input.invoiceId'
            })
          }
        }),
        // Runs after getInvoice so it can resolve the customer id off the
        // invoice output, exposing the full customer (and its custom
        // properties) to downstream conditions.
        createSimpleTask({
          name: 'get_sequence_data',
          taskReferenceName: taskReferenceName('getCustomer'),
          inputParameters: {
            taskExecutorConfiguration: {
              executor: TASK_EXECUTORS.customer,
              version: 1
            },
            data: wrapVariables({
              customerId: `${taskReferenceName('getInvoice')}.output.invoice.customerId`
            })
          }
        })
      ]
    },
    startNodeInputParameters: ['invoiceId'],
    executionSummary: InvoiceSentExecutionSummary,
    providesConditionalProperties: {
      GROSS_TOTAL: {
        label: 'Invoice amount',
        operators: {
          LESS_THAN: {
            label: 'Less than',
            valueInputComponent: invoiceValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: lessThanAmount({
              currencySemanticType: semanticTypes.sequence.invoice.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          },
          MORE_THAN: {
            label: 'More than',
            valueInputComponent: invoiceValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: moreThanAmount({
              currencySemanticType: semanticTypes.sequence.invoice.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          },
          EQUALS: {
            label: 'Equals',
            valueInputComponent: invoiceValueInput,
            gridStyle: amountWithUnitGridStyle,
            functionString: equalsAmount({
              currencySemanticType: semanticTypes.sequence.invoice.currency
            }),
            formatPreviewValue: formatAmountWithUnit
          }
        },
        valueSemanticType: semanticTypes.sequence.invoice.grossTotal
      },
      CUSTOMER: customerConditionalProperty({
        customerIdSemanticType: semanticTypes.sequence.invoice.customerId,
        customPropertyKeys
      })
    }
  }
}
